import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import { authService } from "../../services/api";

const LawyerDetailsStep = () => {
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.signup);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    licenseNumber: "",
    specialization: "",
  });

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Auto-create: only the name is required. License number and
    // specialization are optional here — the lawyer completes the rest later
    // in the "Lawyer profile" settings section.
    if (!formData.name) {
      dispatch(setError("Please enter your full name"));
      return;
    }

    setLoading(true);
    dispatch(setError(null));

    const lawyerData = { name: formData.name };
    if (formData.licenseNumber) lawyerData.license_number = formData.licenseNumber;
    if (formData.specialization) lawyerData.specialization = formData.specialization;

    console.log("Submitting lawyer data:", lawyerData);

    try {
      const response = await authService.createLawyer(lawyerData);

      if (response.success) {
        dispatch(setSignupData({ lawyerDetails: lawyerData }));
        dispatch(setVerificationStep("complete"));
      } else {
        dispatch(
          setError(response.error || "Failed to create lawyer profile")
        );
      }
    } catch (error) {
      console.error("Submit error:", error);
      dispatch(setError("An unexpected error occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="w-full">
        <div className="flex items-center justify-center w-10 h-10 bg-[#374DF5] rounded-lg mb-4">
          <svg
            width="14"
            height="13"
            viewBox="0 0 14 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.26144 11.5897V10.4588C9.26144 9.85886 9.02313 9.28353 8.59894 8.85934C8.17475 8.43515 7.59943 8.19685 6.99953 8.19685H3.0412C2.44131 8.19685 1.86598 8.43515 1.44179 8.85934C1.0176 9.28353 0.779297 9.85886 0.779297 10.4588V11.5897M11.5233 4.23851V7.63137M13.2198 5.93494H9.82692M7.28227 3.67304C7.28227 4.92225 6.26958 5.93494 5.02037 5.93494C3.77115 5.93494 2.75846 4.92225 2.75846 3.67304C2.75846 2.42382 3.77115 1.41113 5.02037 1.41113C6.26958 1.41113 7.28227 2.42382 7.28227 3.67304Z"
              stroke="white"
              stroke-width="1.32577"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h2 className="text-2xl leading-[28px] font-bold text-gray-900 mb-2">
          Lawyer Details
        </h2>
        <p className="text-[14px] leading-[20px] text-[#6C737F] mb-8">
          Please provide your legal practice information
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              Full Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              License Number <span className="text-[#6C737F] font-normal">(optional)</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="Enter your license number"
              value={formData.licenseNumber}
              onChange={(e) => handleInputChange("licenseNumber", e.target.value)}
            />
          </div>

          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              Specialization <span className="text-[#6C737F] font-normal">(optional)</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="Enter your area of specialization"
              value={formData.specialization}
              onChange={(e) => handleInputChange("specialization", e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2283FF] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#1a6cd4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating Profile...
              </div>
            ) : (
              "Continue"
            )}
          </button>
        </form>
      </div>
    </>
  );
};

export default LawyerDetailsStep;
